import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useBranding } from '../Context/BrandingContext';
import { useLoginModal } from '../Context/LoginModalContext';
import { getSessionToken, getUserDetails } from '../Services/services';
import { ROLES, hasRole } from '../utils/roles';
import { getOrders, getOrder, getOrderLines } from '../api/orders';
import AppHeader from './AppHeader';
import PermissionGate from './PermissionGate';
import { isInsufficientPermissionError } from '../utils/permissionErrors';
import { useInfiniteList } from '../hooks/useInfiniteList';
import { usePersonalizations } from '../hooks/usePersonalizations';
import SortChipRow from './marketplace/SortChipRow';

function toUserErrorMessage(error, fallback) {
  const raw = error?.message ? String(error.message) : '';
  if (!raw) return fallback;
  if (/semi join|SOQL|MALFORMED_QUERY|UNEXPECTED_EXCEPTION|tenant key/i.test(raw)) {
    return "We couldn't complete that search. Try a different term, or refresh the page.";
  }
  return raw;
}

// Keyed by the Sales Order Status picklist API values (note underscores). We also
// accept the space-separated label form so the badge renders correctly regardless
// of whether the API value or label is returned.
const BLUE = { color: '#2563eb', bg: '#eff6ff' };
const GREEN = { color: '#059669', bg: '#ecfdf5' };
const AMBER = { color: '#d97706', bg: '#fffbeb' };
const INDIGO = { color: '#7c3aed', bg: '#f5f3ff' };
const CYAN = { color: '#0891b2', bg: '#ecfeff' };
const RED = { color: '#dc2626', bg: '#fef2f2' };

const STATUS_MAP = {
  'New': { label: 'New', ...BLUE },
  'In_Progress': { label: 'In Progress', ...BLUE },
  'In Progress': { label: 'In Progress', ...BLUE },
  'Partially_Allocated': { label: 'Partially Allocated', ...AMBER },
  'Partially Allocated': { label: 'Partially Allocated', ...AMBER },
  'Allocated': { label: 'Allocated', ...INDIGO },
  'Released': { label: 'Released', ...CYAN },
  'Shipped': { label: 'Shipped', ...GREEN },
  'Invoiced': { label: 'Invoiced', ...GREEN },
  'Cancelled': { label: 'Cancelled', ...RED },
};

function mapStatus(sfStatus) {
  // Salesforce may return picklist API names with underscores (e.g. "In_Progress").
  // Normalize to spaces so it matches the map keys and displays cleanly.
  const normalized = (sfStatus || '').replace(/_/g, ' ').trim();
  return STATUS_MAP[normalized] || { label: normalized || 'Unknown', color: '#6b7280', bg: '#f3f4f6' };
}

const SEARCH_DEBOUNCE_MS = 400;
const LINE_FETCH_CONCURRENCY = 3;

const ORDER_SORT_CHIPS = [
  { value: 'date_desc', label: 'Newest first' },
  { value: 'date_asc', label: 'Oldest first' },
  { value: 'name_asc', label: 'SO number (A–Z)' },
  { value: 'name_desc', label: 'SO number (Z–A)' },
  { value: 'po_asc', label: 'PO number (A–Z)' },
  { value: 'po_desc', label: 'PO number (Z–A)' },
];

// Values match the Sales Order Status picklist API names (underscored); labels
// are the space-separated display form, consistent with STATUS_MAP above.
const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'New', label: 'New' },
  { value: 'In_Progress', label: 'In Progress' },
  { value: 'Partially_Allocated', label: 'Partially Allocated' },
  { value: 'Allocated', label: 'Allocated' },
  { value: 'Released', label: 'Released' },
  { value: 'Shipped', label: 'Shipped' },
  { value: 'Invoiced', label: 'Invoiced' },
  { value: 'Cancelled', label: 'Cancelled' },
];

const URGENCY_OPTIONS = [
  { value: '', label: 'All urgencies' },
  { value: 'Routine', label: 'Routine' },
  { value: 'High', label: 'High' },
  { value: 'Low', label: 'Low' },
];

function OrdersPlaceholder() {
  return (
    <div style={{
      backgroundColor: '#ffffff',
      border: '1px solid #e5e7eb',
      borderRadius: 12,
      padding: '20px 24px',
    }}>
      <div style={{ height: 12, width: '40%', backgroundColor: '#e5e7eb', borderRadius: 4, marginBottom: 16 }} />
      <div style={{ height: 72, backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, marginBottom: 12 }} />
      <div style={{ height: 72, backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8 }} />
    </div>
  );
}

export default function MyOrders() {
  const { branding } = useBranding();
  const { openLogin } = useLoginModal();
  const navigate = useNavigate();
  const auth = useSelector((state) => state.auth);

  const [userRole, setUserRole] = useState(auth.role || ROLES.PENDING);
  const [roleReady, setRoleReady] = useState(!!auth.role);

  // `searchInput` is the raw text field value; `searchTerm` is the debounced value
  // actually sent to the server so we don't fire a request on every keystroke.
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('date_desc');
  const [statusFilter, setStatusFilter] = useState('');
  const [urgencyFilter, setUrgencyFilter] = useState('');
  const [portalOnly, setPortalOnly] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const activeFilterCount = [statusFilter, urgencyFilter, portalOnly ? 'portal' : ''].filter(Boolean).length;

  const [expandedIds, setExpandedIds] = useState(new Set());
  const [orderDetail, setOrderDetail] = useState({});
  const [orderLines, setOrderLines] = useState({});
  const [detailLoading, setDetailLoading] = useState({});
  const { prefs, setPref } = usePersonalizations();
  const linesExpanded = !!prefs['orders.linesExpanded'];

  const isGuest = !getSessionToken();
  const isPending = roleReady && !isGuest && !hasRole(userRole, ROLES.STANDARD);

  useEffect(() => {
    if (isGuest) { setRoleReady(true); return; }
    getUserDetails()
      .then((resp) => setUserRole(resp?.data?.role || auth.role || ROLES.PENDING))
      .catch(() => setUserRole(auth.role || ROLES.PENDING))
      .finally(() => setRoleReady(true));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchPage = useCallback((page, limit) => getOrders({
    page,
    limit,
    sort: sortBy,
    keyword: searchTerm || undefined,
    status: statusFilter || undefined,
    urgency: urgencyFilter || undefined,
    portalOnly: portalOnly || undefined,
  }), [sortBy, searchTerm, statusFilter, urgencyFilter, portalOnly]);

  const resetKey = JSON.stringify({ sortBy, searchTerm, statusFilter, urgencyFilter, portalOnly });
  const listEnabled = !isGuest && roleReady && !isPending;
  const {
    records: orders,
    totalCount,
    loading,
    loadingMore,
    error,
    hasMore,
    sentinelRef,
    reload,
  } = useInfiniteList({
    cacheKey: 'orders',
    fetchPage,
    resetKey,
    enabled: listEnabled,
  });

  const orderLinesRef = useRef(orderLines);
  orderLinesRef.current = orderLines;
  const detailLoadingRef = useRef(detailLoading);
  detailLoadingRef.current = detailLoading;

  const loadLines = useCallback(async (orderId) => {
    if (orderLinesRef.current[orderId] || detailLoadingRef.current[orderId]) return;
    setDetailLoading((prev) => ({ ...prev, [orderId]: true }));
    try {
      const [detailResp, linesResp] = await Promise.allSettled([
        getOrder(orderId),
        getOrderLines(orderId),
      ]);
      if (detailResp.status === 'fulfilled') {
        setOrderDetail((prev) => ({ ...prev, [orderId]: detailResp.value }));
      }
      if (linesResp.status === 'fulfilled') {
        const list = Array.isArray(linesResp.value)
          ? linesResp.value
          : (linesResp.value?.lines || linesResp.value?.records || []);
        setOrderLines((prev) => ({ ...prev, [orderId]: list }));
      } else {
        setOrderLines((prev) => ({ ...prev, [orderId]: [] }));
      }
    } finally {
      setDetailLoading((prev) => ({ ...prev, [orderId]: false }));
    }
  }, []);

  const applyExpandAll = useCallback((rows) => {
    const ids = rows.map((o) => o.Id).filter(Boolean);
    setExpandedIds((prev) => {
      const same = ids.length === prev.size && ids.every((id) => prev.has(id));
      return same ? prev : new Set(ids);
    });
  }, []);

  useEffect(() => {
    if (!linesExpanded || orders.length === 0) return undefined;
    applyExpandAll(orders);
    const pending = orders.map((o) => o.Id).filter((id) => id && !orderLinesRef.current[id]);
    if (pending.length === 0) return undefined;
    let cancelled = false;
    const run = async () => {
      for (let i = 0; i < pending.length && !cancelled; i += LINE_FETCH_CONCURRENCY) {
        await Promise.all(pending.slice(i, i + LINE_FETCH_CONCURRENCY).map((id) => loadLines(id)));
      }
    };
    run();
    return () => { cancelled = true; };
  }, [linesExpanded, orders, loadLines, applyExpandAll]);

  const handleExpand = async (orderId) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(orderId)) next.delete(orderId);
      else next.add(orderId);
      return next;
    });
    loadLines(orderId);
  };

  if (isGuest) return null;

  if (isPending) {
    return (
      <div style={styles.page}>
        <AppHeader title="Orders" />
        <main style={styles.main}>
          <div style={styles.container}>
            <PermissionGate blocked primaryColor={branding.primaryColor} minHeight={380}>
              <OrdersPlaceholder />
            </PermissionGate>
          </div>
        </main>
      </div>
    );
  }

  const permissionBlocked = isInsufficientPermissionError(error);
  const hasActiveFilter = !!searchTerm || portalOnly || !!statusFilter || !!urgencyFilter;
  const isTrulyEmpty = !loading && totalCount === 0 && !hasActiveFilter;
  const hasNoMatches = !loading && totalCount === 0 && hasActiveFilter;

  return (
    <div style={styles.page}>
      <AppHeader title="Orders" />

      <main style={styles.main}>
        <div style={styles.container}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 12 }}>
            <button
              type="button"
              style={styles.refreshBtn}
              onClick={() => {
                const next = !linesExpanded;
                setPref('orders.linesExpanded', next);
                if (next) applyExpandAll(orders);
                else setExpandedIds(new Set());
              }}
            >
              {linesExpanded ? 'Collapse all' : 'Expand all'}
            </button>
            <button type="button" style={styles.refreshBtn} onClick={reload} disabled={loading} title="Refresh from Salesforce">
              {loading ? '…' : '↻ Refresh'}
            </button>
          </div>

          {/* Search — server-side, matches SO number, PO number, or part number */}
          <div style={styles.searchRow}>
            <div style={styles.searchWrap}>
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search by SO number, PO number, or Part Number…"
                style={styles.searchInput}
              />
              {loading && searchInput && (
                <span style={styles.searchSpinner} aria-label="Searching">
                  <span className="spinner-inline" />
                </span>
              )}
            </div>
            <button
              type="button"
              style={filtersOpen ? styles.filtersToggleBtnActive : styles.filtersToggleBtn}
              onClick={() => setFiltersOpen((v) => !v)}
              aria-expanded={filtersOpen}
            >
              Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
              <span style={{
                ...styles.expandChevron,
                transform: filtersOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              }}>
                <svg viewBox="0 0 24 24" fill="none" width={14} height={14}
                  stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m6 9 6 6 6-6"/>
                </svg>
              </span>
            </button>
          </div>

          <SortChipRow
            options={ORDER_SORT_CHIPS}
            value={sortBy}
            onChange={setSortBy}
            primaryColor={branding.primaryColor}
          />

          {/* Filters — applied server-side */}
          {filtersOpen && (
            <div style={styles.filterPanel}>
              <div style={styles.filterField}>
                <label style={styles.filterLabel}>Status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => { setStatusFilter(e.target.value); }}
                  style={styles.sortSelect}
                >
                  {STATUS_FILTER_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div style={styles.filterField}>
                <label style={styles.filterLabel}>Urgency</label>
                <select
                  value={urgencyFilter}
                  onChange={(e) => { setUrgencyFilter(e.target.value); }}
                  style={styles.sortSelect}
                >
                  {URGENCY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <label style={styles.toggleLabel}>
                <input
                  type="checkbox"
                  checked={portalOnly}
                  onChange={(e) => { setPortalOnly(e.target.checked); }}
                />
                Portal orders only{portalOnly ? ` (${totalCount})` : ''}
              </label>
            </div>
          )}

          {loading && <p>Loading orders&hellip;</p>}

          <PermissionGate blocked={permissionBlocked} primaryColor={branding.primaryColor} minHeight={320}>
            {isTrulyEmpty && (
              <div style={styles.emptyCard}>
                <p style={styles.muted}>
                  You have no purchase orders yet. Purchase ready items from your <Link to="/cart">cart</Link>.
                </p>
              </div>
            )}

            {hasNoMatches && (
              <div style={styles.emptyCard}>
                <p style={styles.muted}>No orders match your search or filter.</p>
              </div>
            )}

            {!loading && orders.length > 0 && orders.map((o) => {
            const status = mapStatus(o.Status || o.inscor__Status__c);
            const isExpanded = expandedIds.has(o.Id);
            const lines = orderLines[o.Id] || [];
            const busy = detailLoading[o.Id];

            const poNumber = o.PONumber || o.inscor__Customer_PO_Number__c || '—';
            const urgency = o.inscor__Urgency__c || '—';
            const numberOfLines = o.LineCount ?? o.inscor__Number_of_Lines__c ?? o.inscor__Line_Count__c ?? '—';
            const total = o.Total ?? o.inscor__Sales_Order_Total_Value__c ?? o.inscor__Total__c;
            const createDate = o.inscor__Create_Date__c
              ? new Date(o.inscor__Create_Date__c).toLocaleDateString()
              : (o.CreatedDate ? new Date(o.CreatedDate).toLocaleDateString() : '—');

            return (
              <div key={o.Id} style={styles.orderCard}>
                <div
                  style={{ ...styles.orderHeader, cursor: 'pointer' }}
                  onClick={() => handleExpand(o.Id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleExpand(o.Id); } }}
                  aria-expanded={isExpanded}
                >
                  <div style={styles.orderLeft}>
                    <span style={styles.orderName}>{o.Name}</span>
                    <span style={{
                      ...styles.statusBadge,
                      color: status.color,
                      backgroundColor: status.bg,
                    }}>
                      {status.label}
                    </span>
                  </div>
                  <div style={styles.orderRight}>
                    <span style={styles.linesHint}>{isExpanded ? 'Hide lines' : 'View lines'}</span>
                    <span style={{ ...styles.expandChevron, transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                      <svg viewBox="0 0 24 24" fill="none" width={18} height={18}
                        stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="m6 9 6 6 6-6"/>
                      </svg>
                    </span>
                  </div>
                </div>
                <div style={styles.detailRow}>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>PO Number</span>
                    <span style={styles.detailValue}>{poNumber}</span>
                  </div>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>Urgency</span>
                    <span style={styles.detailValue}>{urgency}</span>
                  </div>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>Number of Lines</span>
                    <span style={styles.detailValue}>{numberOfLines}</span>
                  </div>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>Total</span>
                    <span style={styles.detailValue}>
                      {total != null ? `$${Number(total).toFixed(2)}` : '—'}
                    </span>
                  </div>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>Create Date</span>
                    <span style={styles.detailValue}>{createDate}</span>
                  </div>
                </div>

                {isExpanded && (
                  <div style={styles.orderBody}>
                    {busy && <p style={styles.muted}>Loading lines&hellip;</p>}
                    {!busy && lines.length === 0 && (
                      <p style={styles.muted}>No lines found.</p>
                    )}
                    {!busy && lines.length > 0 && (
                      <div style={styles.lineTableWrap}>
                        <table style={styles.lineTable}>
                          <thead>
                            <tr>
                              <th style={styles.lineTh}>Part</th>
                              <th style={styles.lineTh}>Condition</th>
                              <th style={styles.lineTh}>Qty</th>
                              <th style={styles.lineTh}>Price</th>
                              <th style={styles.lineTh}>Total</th>
                              <th style={styles.lineTh}>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {lines.map((l, idx) => (
                              <tr key={l.Id || idx} style={idx % 2 === 0 ? styles.lineRowEven : undefined}>
                                <td style={styles.lineTd}>
                                  {l.ProductName || l.inscor__Product__c?.Name || '—'}
                                </td>
                                <td style={styles.lineTd}>
                                  {l.Condition || l.inscor__Condition_Code__c?.Name || '—'}
                                </td>
                                <td style={styles.lineTd}>
                                  {l.Quantity ?? l.inscor__Quantity_Ordered__c ?? '—'}
                                </td>
                                <td style={styles.lineTd}>
                                  {l.Price != null || l.inscor__Price__c != null
                                    ? `$${Number(l.Price ?? l.inscor__Price__c).toFixed(2)}`
                                    : '—'}
                                </td>
                                <td style={styles.lineTd}>
                                  {l.LineTotal != null || l.inscor__Line_Total__c != null
                                    ? `$${Number(l.LineTotal ?? l.inscor__Line_Total__c).toFixed(2)}`
                                    : '—'}
                                </td>
                                <td style={styles.lineTd}>
                                  {(l.Status || l.inscor__Status__c)
                                    ? (l.Status || l.inscor__Status__c).replace(/_/g, ' ')
                                    : '—'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          </PermissionGate>

          <div ref={sentinelRef} style={{ height: 1 }} />
          {loadingMore && <p style={styles.muted}>Loading more orders…</p>}
          {!loading && !hasMore && totalCount > 0 && (
            <p style={{ ...styles.muted, textAlign: 'center', marginTop: 16 }}>No more orders</p>
          )}

          {error && !permissionBlocked && (
            <p style={styles.err}>{toUserErrorMessage(error, 'Failed to load orders')}</p>
          )}
        </div>
      </main>
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', backgroundColor: 'var(--background)', color: 'var(--foreground)' },
  refreshBtn: {
    border: '1px solid #d1d5db',
    background: 'transparent',
    color: '#374151',
    borderRadius: 8,
    padding: '8px 14px',
    fontWeight: 600,
    cursor: 'pointer',
    fontSize: 13,
  },
  main: { padding: '24px 24px' },
  container: { maxWidth: 1100, margin: '0 auto' },
  err: { color: 'var(--destructive)', fontSize: 14 },
  muted: { color: 'var(--muted-foreground)', fontSize: 14 },
  emptyCard: {
    backgroundColor: 'var(--card)',
    border: '1px solid var(--border)',
    borderRadius: 12,
    padding: 24,
  },

  searchRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 12,
    marginBottom: 12,
  },
  filtersToggleBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    border: '1px solid var(--border)',
    background: 'var(--card)',
    color: 'var(--foreground)',
    borderRadius: 8,
    padding: '8px 14px',
    fontWeight: 600,
    fontSize: 13,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  filtersToggleBtnActive: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    border: '1px solid var(--primary)',
    background: 'var(--secondary)',
    color: 'var(--foreground)',
    borderRadius: 8,
    padding: '8px 14px',
    fontWeight: 600,
    fontSize: 13,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  filterPanel: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-end',
    gap: 16,
    backgroundColor: 'var(--secondary)',
    border: '1px solid var(--border)',
    borderRadius: 10,
    padding: '14px 16px',
    marginBottom: 16,
  },
  filterField: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    minWidth: 160,
  },
  filterLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: 'var(--muted-foreground)',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  searchWrap: {
    position: 'relative',
    flex: '1 1 240px',
    minWidth: 200,
    display: 'flex',
    alignItems: 'center',
  },
  searchSpinner: {
    position: 'absolute',
    right: 14,
    top: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    pointerEvents: 'none',
  },
  searchInput: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '8px 36px 8px 12px',
    borderRadius: 8,
    border: '1px solid var(--border)',
    fontSize: 13,
    backgroundColor: 'var(--card)',
    color: 'var(--foreground)',
  },
  sortSelect: {
    padding: '8px 12px',
    borderRadius: 8,
    border: '1px solid var(--border)',
    fontSize: 13,
    backgroundColor: 'var(--card)',
    color: 'var(--foreground)',
  },
  toggleLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 13,
    fontWeight: 500,
    color: 'var(--foreground)',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
  },
  pager: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
    marginTop: 20,
  },
  pagerBtn: {
    border: '1px solid #d1d5db',
    background: 'transparent',
    color: '#374151',
    borderRadius: 8,
    padding: '6px 14px',
    fontWeight: 600,
    fontSize: 13,
    cursor: 'pointer',
  },
  pagerBtnDisabled: {
    border: '1px solid #e5e7eb',
    background: '#f3f4f6',
    color: '#9ca3af',
    borderRadius: 8,
    padding: '6px 14px',
    fontWeight: 600,
    fontSize: 13,
    cursor: 'not-allowed',
  },
  pagerInfo: { fontSize: 13, color: 'var(--muted-foreground)', fontWeight: 500 },

  orderCard: {
    backgroundColor: 'var(--card)',
    border: '1px solid var(--border)',
    borderRadius: 12,
    marginBottom: 12,
    overflow: 'hidden',
  },
  orderHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    cursor: 'pointer',
    gap: 12,
    flexWrap: 'wrap',
    userSelect: 'none',
  },
  orderLeft: { display: 'flex', alignItems: 'center', gap: 10 },
  orderName: { fontWeight: 700, fontSize: 15 },
  statusBadge: {
    display: 'inline-block',
    borderRadius: 6,
    padding: '3px 10px',
    fontSize: 12,
    fontWeight: 600,
  },
  orderRight: { display: 'flex', alignItems: 'center', gap: 16 },
  linesHint: { fontSize: 12, fontWeight: 600, color: 'var(--muted-foreground)' },
  expandChevron: {
    display: 'flex', alignItems: 'center',
    color: 'var(--muted-foreground)',
    transition: 'transform 0.18s ease',
  },

  detailRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 24,
    padding: '0 20px 14px',
    borderBottom: 'none',
  },
  detailItem: { display: 'flex', flexDirection: 'column', gap: 2, minWidth: 100 },
  detailLabel: { fontSize: 11, fontWeight: 600, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: '0.04em' },
  detailValue: { fontSize: 14, fontWeight: 500, color: 'var(--foreground)' },

  orderBody: {
    borderTop: '1px solid var(--border)',
    padding: '16px 20px',
  },

  lineTableWrap: { overflowX: 'auto' },
  lineTable: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  lineTh: {
    textAlign: 'left',
    padding: '8px 10px',
    backgroundColor: 'var(--muted)',
    fontWeight: 600,
    borderBottom: '2px solid var(--border)',
    whiteSpace: 'nowrap',
  },
  lineTd: { padding: '8px 10px', borderBottom: '1px solid var(--border)' },
  lineRowEven: { backgroundColor: 'var(--secondary)' },
};
